import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../common/api";

/**
 * Loads the Alignment Unit list once, at the top level (see VisualizerContent.js) rather than
 * per-toolbar - both Scan Analysis and Raw Surface Analysis share this same fetch/state.
 *
 * A 401/403 here is treated distinctly from any other failure: it means the logged-in user
 * lacks the "dv" permission, which VisualizerContent.js uses to show a dedicated
 * not-authorized message instead of the normal tab UI.
 */
export const loadAU = createAsyncThunk(
    "scans/loadAU",
    async (_args, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            return await new api().request(`/datavisualizer/au/1`, token); // 1 = company id for RedAlign
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                return rejectWithValue({ unauthorized: true });
            }
            return rejectWithValue({ message: err?.message ?? "Failed to load Alignment Units" });
        }
    }
);

export const ScansSlice = createSlice({
    name:'scans',
    initialState: {
        au: [],
        auStatus: "idle", // idle | loading | ready | error | unauthorized
        auError: null,
        data: [],
        show: false,
    },
    reducers: {
        loadScans: (state, action) => {
            state.data = action.payload.scans;
        },
        showScans: (state, action) => {
            state.show = action.payload.show;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(loadAU.pending, (state) => {
                state.auStatus = "loading";
                state.auError = null;
            })
            .addCase(loadAU.fulfilled, (state, action) => {
                state.au = action.payload;
                state.auStatus = "ready";
            })
            .addCase(loadAU.rejected, (state, action) => {
                state.auStatus = action.payload?.unauthorized ? "unauthorized" : "error";
                state.auError = action.payload?.message ?? null;
            });
    },
})

export const { loadScans, showScans } = ScansSlice.actions

export default ScansSlice.reducer